import { RucJuridica } from './validationPanama'

const validators = {
  CHILE: validateRut,
  PERU: validateRucPeru,
  ARGENTINA: validateCuit,
  PANAMA: validateRucPanama,
  COLOMBIA: validateNit,
  'ESTADOS UNIDOS': validateEin,
  MEXICO: validateRfcMexico,
  AUSTRALIA: validateEin,
  ZAMBIA: validateTpin,
  SUIZA: validateUidCH
}

function normalizeCountry (country) {
  return String(country || '')
    .trim()
    .normalize('NFD') // elimina acentos
    .replace(/[\u0300-\u036f]/g, '') // quita diacríticos
    .toUpperCase()
}

function validateTaxId (taxId, country, manualValidation) {
  if (manualValidation) {
    return true
  }
  
  const countryUpper = normalizeCountry(country)
  const validator = validators[countryUpper]
  return validator ? validator(taxId) : false
}

function taxIdCleaner (taxId, country) {
  if (normalizeCountry(country) === 'CHILE') {
    return taxId.replace(/\./g, '').toUpperCase()
  }
  return taxId
}

function taxIdGobValidation (record) {
  const country = normalizeCountry(record.country)
  if (['ARGENTINA', 'PERU'].includes(country)) {
    return true
  }
  return true // siempre retorna true en el código original
}

// CUIT (Argentina)
function validateCuit (cuit) {
  if (!cuit.includes('-')) return false

  const pattern = /^\d{2}-\d{8}-\d{1}$/
  if (!pattern.test(cuit)) return false

  const cleanCuit = cuit.replace(/-/g, '')
  if (cleanCuit.length !== 11 || isNaN(cleanCuit)) return false

  const base = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2]
  const aux =
    cleanCuit
      .slice(0, 10)
      .split('')
      .reduce((acc, digit, i) => acc + Number(digit) * base[i], 0) % 11

  const lastDigit = cleanCuit[10]
  return (
    aux === 11 - Number(lastDigit) ||
    (aux === 1 && lastDigit === '9') ||
    (aux === 0 && lastDigit === '0')
  )
}

// RUT (Chile)
function validateRut (rut) {
  if (!rut.includes('-')) return false

  const cleanRut = rut.replace(/\./g, '').replace('-', '')
  const rutBody = cleanRut.slice(0, -1)
  const dv = cleanRut.slice(-1).toUpperCase()

  if (!/^\d+$/.test(rutBody) || !'0123456789K'.includes(dv)) return false

  const reversedDigits = rutBody
    .split('')
    .reverse()
    .map(Number)
  const factors = [2, 3, 4, 5, 6, 7]
  const sum = reversedDigits.reduce(
    (acc, digit, i) => acc + digit * factors[i % factors.length],
    0
  )

  const dvCalc = 11 - (sum % 11)
  if (dvCalc === 11) return dv === '0'
  if (dvCalc === 10) return dv === 'K'
  return String(dvCalc) === dv
}

// RUC (Perú)
function validateRucPeru (ruc) {
  const cleanRuc = ruc.replace(/[.-]/g, '')
  if (!/^\d{11}$/.test(cleanRuc)) return false

  const factors = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2]
  const sum = cleanRuc
    .slice(0, 10)
    .split('')
    .reduce((acc, digit, i) => acc + Number(digit) * factors[i], 0)

  let dv = 11 - (sum % 11)
  if (dv === 10) dv = 0
  if (dv === 11) dv = 1

  return Number(cleanRuc[10]) === dv
}

/**
 * Validación de NIT Colombia (DIAN - módulo 11)
 * @param nit Tax ID
 * @returns {boolean}
 */
function validateNit (nit) {
  const clean = String(nit).replace(/\D/g, '')
  if (clean.length < 2) return false

  const base = clean.slice(0, -1)
  const dv = clean.slice(-1)

  if (!/^\d+$/.test(base) || !/^\d$/.test(dv)) return false

  const factores = [71, 67, 59, 53, 47, 43, 41, 37, 29, 23, 19, 17, 13, 7, 3]
  let suma = 0
  let j = factores.length - 1

  for (let i = base.length - 1; i >= 0; i--) {
    suma += Number(base[i]) * factores[j]
    j--
    if (j < 0) j = factores.length - 1
  }

  const r = suma % 11
  const calculado = r > 1 ? 11 - r : r

  return String(calculado) === dv
}

// RUC (Panamá)
function validateRucPanama (ruc) {
  // Expresión regular para buscar "DV", "dv" con o sin ":" y espacios
  const dvRegex = /\s*DV\s*:?\s*/i
  const hasDV = dvRegex.test(ruc)

  const parts = ruc.replace(dvRegex, '-').split('-').map(p => p.trim()).filter(p => p !== '')
  if (parts.length < 3) return false

  const baseRuc = `${parts[0]}-${parts[1]}-${parts[2]}`
  if (hasDV) {
    const rucValidator = new RucJuridica()
    const dvIntroducido = parts[3]

    if (!dvIntroducido) return false

    const dvCalculado = rucValidator._calculateDv(baseRuc)
    return String(dvCalculado) === String(dvIntroducido)
  }

  return /^\d+-\d+-\d+$/.test(baseRuc)
}

// EIN (EE.UU. y Australia)
function validateEin (ein) {
  if (!ein.includes('-')) return false
  return /^\d{2}-\d{7}$/.test(ein)
}

// RFC (México)
function validateRfcMexico (rfc) {
  const patron = /^[A-ZÑ&]{3,4}\d{6}[A-Z\d]{3}$/
  return patron.test(rfc)
}

// TPIN (Zambia)
function validateTpin (taxId) {
  return /^\d{10}$/.test(taxId)
}

/**
 * Validación para Suiza (UID - CHE)
 * Formato: CHE-123.456.789
 */
function validateUidCH (uid) {
  const upperUid = String(uid).toUpperCase()
  const clean = upperUid.replace(/\D/g, '')

  if (clean.length !== 9) return false

  const digits = clean.split('').map(Number)
  const validateDigit = digits.pop()

  const factors = [5, 4, 3, 2, 7, 6, 5, 4]

  let sum = 0
  for (let i = 0; i < digits.length; i++) {
    sum += digits[i] * factors[i]
  }

  const remainder = sum % 11
  let calculatedDv

  if (remainder === 0) {
    calculatedDv = 0
  } else {
    calculatedDv = 11 - remainder
  }

  if (calculatedDv === 10) return false

  const isValid = calculatedDv === validateDigit

  if (!isValid) return false

  const hasMwst = / MWST\s*$/i.test(upperUid)
  const hasTva = / TVA\s*$/i.test(upperUid)
  const hasIva = / IVA\s*$/i.test(upperUid)

  const hasSuffix = / [A-Z]+\s*$/i.test(upperUid)

  if (!hasSuffix) {
    return isValid
  }

  return isValid && (hasMwst || hasTva || hasIva)
}

// Email
function validateEmail (email) {
  const patron = /^[\w.-]+@[\w.-]+\.\w+$/
  return patron.test(email)
}

export { validateTaxId, taxIdCleaner, taxIdGobValidation, validateEmail }



// WEBPACK FOOTER //
// ./src/utils/validationHelpers.js